import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import {
  format,
  parseISO,
  isValid,
  isAfter,
  endOfDay,
  subDays,
  addDays,
  compareAsc,
} from 'date-fns';
import { Tour } from 'app/models/Tour';
import SiteWrapper from 'app/containers/SiteWrapper';
import { selectPartOrderEntities } from 'store/slices/partOrderSlice';
import {
  useGetActiveToursQuery,
  useGetAllVehiclesQuery,
  useGetGeofenceEventsQuery,
  useGetDeliveriesForToursQuery,
  useGetVehicleLocationsQuery,
  useGetTourQuery,
  useGetTourHistoryQuery,
} from 'services/kewloxApi';
import { Waypoints } from 'lucide-react';

// --- Leaflet Imports ---
import { useMap } from 'react-leaflet';
import L, { LatLngExpression, LatLngBoundsExpression } from 'leaflet';
import 'leaflet/dist/leaflet.css';

// --- Leaflet Icon Fix ---
// @ts-ignore
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';

// @ts-ignore
delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: iconRetinaUrl,
  iconUrl: iconUrl,
  shadowUrl: shadowUrl,
});

import {
  isDelivered,
  isNotDelivered,
  isFinalStatus,
  blueIcon,
  greenIcon,
  greyIcon,
  getDeliveryStopName,
} from 'app/containers/FleetTracking/fleetTrackingHelpers';
import FleetTourSidebar from 'app/containers/FleetTracking/FleetTourSidebar';
import FleetMap from 'app/containers/FleetTracking/FleetMap';
import FleetTimeline from 'app/containers/FleetTracking/FleetTimeline';
import FleetTourHeader from 'app/containers/FleetTracking/FleetTourHeader';

const FitBoundsToMarkers: React.FC<{
  bounds?: LatLngBoundsExpression;
  isLive: boolean; // <-- Ajouter cette prop
}> = ({ bounds, isLive }) => {
  const map = useMap();
  useEffect(() => {
    if (!isLive && bounds && map) {
      try {
        map.fitBounds(bounds, { padding: [50, 50] });
      } catch (e) {
        console.error('Error fitting bounds:', e);
      }
    }
  }, [bounds, map, isLive]);
  return null;
};

const MapAutoCenter: React.FC<{
  center: LatLngExpression | null;
  isLive: boolean;
}> = ({ center, isLive }) => {
  const map = useMap();

  useEffect(() => {
    if (isLive && center) {
      map.panTo(center, { animate: true, duration: 1 });
    }
  }, [center, isLive, map]);

  return null;
};

export default function FleetTrackingPage() {
  const { data: tours, isLoading: isLoadingTours } = useGetActiveToursQuery();
  const { data: vehicles } = useGetAllVehiclesQuery();
  const partOrdersEntities = useSelector(selectPartOrderEntities);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce for search (400ms)
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => {
      clearTimeout(handler);
    };
  }, [search]);

  const searchId = Number.parseInt(debouncedSearch, 10);
  const shouldFetchTour = !Number.isNaN(searchId) && searchId > 1000;
  const { data: searchedTour, isLoading: isLoadingSingleTour } =
    useGetTourQuery(searchId, {
      skip: !shouldFetchTour,
    });

  const [dateRange, setDateRange] = useState([
    {
      startDate: subDays(new Date(), 7),
      endDate: new Date(),
      key: 'selection',
    },
  ]);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedTourId, setSelectedTourId] = useState<number | null>(null);

  // Fetch tours for the selected date range (like in HistoryPage)
  const { data: toursHistory, isLoading: isLoadingToursHistory } =
    useGetTourHistoryQuery({
      start: format(dateRange[0].startDate, 'yyyy-MM-dd'),
      end: format(addDays(dateRange[0].endDate, 1), 'yyyy-MM-dd'),
    });

  const [locationIndex, setLocationIndex] = useState(0);
  const [isFollowingLive, setIsFollowingLive] = useState(true);

  const toursWithSearched = useMemo(() => {
    // Combine active tours with history tours
    const allTours: Tour[] = [];

    // Add active tours
    if (tours) {
      allTours.push(...tours);
    }

    // Add history tours (avoiding duplicates)
    if (toursHistory) {
      const existingIds = new Set(allTours.map(t => t.id));
      const uniqueHistoryTours = toursHistory.filter(
        t => !existingIds.has(t.id),
      );
      allTours.push(...uniqueHistoryTours);
    }

    // Add searched tour if not already present
    if (searchedTour && !allTours.find(t => t.id === searchedTour.id)) {
      allTours.push(searchedTour);
    }

    return allTours;
  }, [tours, toursHistory, searchedTour]);

  const filteredTours = useMemo(() => {
    if (!toursWithSearched || toursWithSearched.length === 0) return [];
    return toursWithSearched.filter(tour => {
      if (tour.carrier?.id !== 1) return false;

      const tourDateObj = parseISO(tour.date);
      if (isValid(tourDateObj) && isAfter(tourDateObj, endOfDay(new Date()))) {
        return false;
      }

      const driverName = tour.drivers[0]?.name || '';

      const matchesSearch =
        search === '' ||
        tour.id.toString().includes(search) ||
        driverName.toLowerCase().includes(search.toLowerCase()) ||
        (tour.carrier?.name || '').toLowerCase().includes(search.toLowerCase());

      // Check if tour date is within the selected date range
      const matchesDateRange = (() => {
        if (!isValid(tourDateObj)) return false;
        const startDate = dateRange[0].startDate;
        const endDate = endOfDay(dateRange[0].endDate);
        return (
          compareAsc(tourDateObj, startDate) >= 0 &&
          compareAsc(tourDateObj, endDate) <= 0
        );
      })();

      return matchesSearch && matchesDateRange;
    });
  }, [toursWithSearched, search, dateRange]);

  const { isLoading: isLoadingDeliveries } = useGetDeliveriesForToursQuery(
    filteredTours.map(t => t.id),
    {
      skip: !filteredTours || filteredTours.length === 0,
    },
  );

  const isDeliveriesDataReady =
    !isLoadingDeliveries && filteredTours.length > 0;

  const selectedTour = useMemo(() => {
    if (!selectedTourId && filteredTours.length > 0) {
      return filteredTours[0];
    }
    return filteredTours.find(t => t.id === selectedTourId) || null;
  }, [filteredTours, selectedTourId]);

  const selectedVehicle = useMemo(() => {
    if (!vehicles || !selectedTour) return undefined;
    return vehicles.find(v => v.id === selectedTour.vehicleId);
  }, [vehicles, selectedTour]);

  const { data: vehicleLocations, isLoading: isLoadingLocations } =
    useGetVehicleLocationsQuery(
      { tour: selectedTour?.id },
      {
        skip: !selectedTour,
        pollingInterval: selectedTour && !selectedTour.delivered ? 10000 : 0,
      },
    );

  const { data: geofenceEvents } = useGetGeofenceEventsQuery(
    { tour: selectedTour?.id },
    { skip: !selectedTour },
  );

  useEffect(() => {
    if (isFollowingLive && vehicleLocations && vehicleLocations.length > 0) {
      setLocationIndex(vehicleLocations.length - 1);
    }
  }, [vehicleLocations, isFollowingLive]);

  useEffect(() => {
    if (!selectedTourId && filteredTours.length > 0) {
      setSelectedTourId(filteredTours[0].id);
    }
    if (
      selectedTourId &&
      !filteredTours.find(t => t.id === selectedTourId) &&
      filteredTours.length > 0
    ) {
      setSelectedTourId(filteredTours[0].id);
    }
    if (filteredTours.length === 0) {
      setSelectedTourId(null);
    }

    setIsFollowingLive(true);
  }, [filteredTours, selectedTourId, isDeliveriesDataReady]);

  const handleSliderChange = (value: number[]) => {
    setIsFollowingLive(false);
    setLocationIndex(value[0]);
  };

  const handleSliderCommit = (value: number[]) => {
    if (value[0] === (vehicleLocations?.length ?? 1) - 1) {
      setIsFollowingLive(true);
    }
  };

  const currentStopForSelectedTour = useMemo(() => {
    if (!selectedTour) return null;
    const sortedDeliveries = [...selectedTour.orders].sort(
      (a, b) => a.position - b.position,
    );
    return sortedDeliveries.find(d => !isFinalStatus(d)) || null;
  }, [selectedTour]);

  const geofenceInfoMap = useMemo(() => {
    const map: Record<
      number,
      { arrival?: string; departure?: string; duration?: number }
    > = {};
    if (!geofenceEvents) return map;
    geofenceEvents.forEach(ev => {
      if (ev.zoneType !== 'client') return;
      const info = map[ev.zoneId] || {};
      if (ev.eventType === 'enter' && !info.arrival) {
        info.arrival = new Date(ev.timestamp).toISOString();
      }
      if (ev.eventType === 'exit') {
        info.departure = new Date(ev.timestamp).toISOString();
      }
      map[ev.zoneId] = info;
    });
    Object.keys(map).forEach(key => {
      const info = map[Number(key)];
      if (info.arrival && info.departure) {
        info.duration = Math.round(
          (new Date(info.departure).getTime() -
            new Date(info.arrival).getTime()) /
            60000,
        );
      }
    });
    return map;
  }, [geofenceEvents]);

  const { mapCenter, mapMarkers, plannedRoutePolyline, mapBounds } =
    useMemo(() => {
      const defaultCenter: LatLngExpression = [50.56828, 4.903442];
      if (!selectedTour)
        return {
          mapCenter: defaultCenter,
          mapMarkers: [],
          plannedRoutePolyline: [],
          mapBounds: undefined,
        };

      const validDeliveriesWithCoords = selectedTour.orders.filter(
        d => d.infos?.lat != null && d.infos?.lng != null,
      );

      if (validDeliveriesWithCoords.length === 0) {
        return {
          mapCenter: defaultCenter,
          mapMarkers: [],
          plannedRoutePolyline: [],
          mapBounds: undefined,
        };
      }

      const sortedValidDeliveries = [...validDeliveriesWithCoords].sort(
        (a, b) => a.position - b.position,
      );

      const markers = sortedValidDeliveries.map(delivery => {
        const position: LatLngExpression = [
          delivery.infos!.lat!,
          delivery.infos!.lng!,
        ];
        let icon;
        if (isDelivered(delivery)) {
          icon = greenIcon;
        } else if (isNotDelivered(delivery)) {
          icon = greyIcon;
        } else if (currentStopForSelectedTour?.id === delivery.id) {
          icon = blueIcon;
        } else {
          icon = greyIcon;
        }

        const popupName = getDeliveryStopName(
          delivery.infos,
          delivery.position,
        );
        const popupAddress = `${delivery.infos?.address1 || ''}, ${
          delivery.infos?.city || ''
        }`;

        return {
          position,
          popupText: `${popupName}<br/>${popupAddress}`,
          icon,
          id: delivery.id,
        };
      });

      const polylineCoords = sortedValidDeliveries.map(
        delivery =>
          [delivery.infos!.lat!, delivery.infos!.lng!] as LatLngExpression,
      );

      const firstDeliveryCoords = sortedValidDeliveries[0]?.infos;
      const center: LatLngExpression = firstDeliveryCoords
        ? [firstDeliveryCoords.lat!, firstDeliveryCoords.lng!]
        : defaultCenter;

      let bounds: LatLngBoundsExpression | undefined = undefined;
      if (polylineCoords.length > 0) {
        bounds = L.latLngBounds(polylineCoords);
      }

      return {
        mapCenter: center,
        mapMarkers: markers,
        plannedRoutePolyline: polylineCoords,
        mapBounds: bounds,
      };
    }, [selectedTour, currentStopForSelectedTour]);

  const vehiclePosition = useMemo(() => {
    if (!vehicleLocations || vehicleLocations.length === 0) return null;
    const loc =
      vehicleLocations[Math.min(locationIndex, vehicleLocations.length - 1)];
    return {
      position: [loc.latitude, loc.longitude] as LatLngExpression,
      time: new Date(loc.timestamp).toISOString(),
    };
  }, [vehicleLocations, locationIndex]);

  const truckPathPolyline = useMemo(() => {
    if (!vehicleLocations || vehicleLocations.length === 0) {
      return [];
    }
    const locationsToShow = vehicleLocations.slice(0, locationIndex + 1);
    return locationsToShow.map(
      loc => [loc.latitude, loc.longitude] as LatLngExpression,
    );
  }, [vehicleLocations, locationIndex]);

  // console.log('--- Rendu du composant ---');
  // console.log('Tours filtrés:', filteredTours);
  // console.log('Tournée sélectionnée:', selectedTour);
  // console.log('Chargement des localisations:', isLoadingLocations);
  // console.log('Données de localisation (vehicleLocations):', vehicleLocations)

  const hasMapData =
    mapMarkers.length > 0 || (vehicleLocations && vehicleLocations.length > 0);

  if (isLoadingTours || isLoadingToursHistory) {
    return (
      <SiteWrapper>
        <div className="p-6 text-center">Chargement des tournées...</div>
      </SiteWrapper>
    );
  }

  return (
    <>
      <Helmet>
        <title>Gestion de flotte</title>
        <meta
          name="description"
          content="Surveillez tous les véhicules de livraison actifs."
        />
      </Helmet>
      <SiteWrapper>
        <div className="p-4 md:p-6">
          <div className="mb-6">
            <h1 className="text-3xl font-bold flex items-center">
              <Waypoints className="mr-3 h-8 w-8 text-blue-600" /> Gestion de
              flotte
            </h1>
            <p className="text-gray-600 mt-1">
              Suivi des véhicules de livraison
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
            <div className="md:col-span-1 lg:col-span-1">
              <FleetTourSidebar
                filteredTours={filteredTours}
                selectedTour={selectedTour}
                search={search}
                dateRange={dateRange}
                showDatePicker={showDatePicker}
                onSearchChange={setSearch}
                onDateRangeChange={setDateRange}
                onToggleDatePicker={() => setShowDatePicker(!showDatePicker)}
                onTourSelect={setSelectedTourId}
                vehicles={vehicles || []}
                isLoadingTours={isLoadingSingleTour}
              />
            </div>

            <div className="md:col-span-2 lg:col-span-3">
              {selectedTour ? (
                <div className="bg-white p-4 sm:p-6 border rounded-lg shadow-sm h-full flex flex-col">
                  <FleetTourHeader
                    selectedTour={selectedTour}
                    selectedVehicle={selectedVehicle}
                    currentStopForSelectedTour={currentStopForSelectedTour}
                  />

                  {isLoadingLocations ? (
                    <div className="flex-1 flex items-center justify-center">
                      <p className="text-gray-500">
                        Chargement des détails de la tournée...
                      </p>
                    </div>
                  ) : (
                    <div className="flex flex-col lg:flex-row gap-6 flex-1 min-h-0">
                      <div className="lg:w-2/3 h-auto flex flex-col">
                        <h3 className="text-lg font-semibold text-gray-700 mb-3">
                          Position et itinéraire
                        </h3>
                        <FleetMap
                          mapCenter={mapCenter}
                          mapMarkers={mapMarkers}
                          plannedRoutePolyline={plannedRoutePolyline}
                          truckPathPolyline={truckPathPolyline}
                          mapBounds={mapBounds}
                          vehiclePosition={vehiclePosition}
                          isFollowingLive={isFollowingLive}
                          locationIndex={locationIndex}
                          vehicleLocations={vehicleLocations || []}
                          handleSliderChange={handleSliderChange}
                          handleSliderCommit={handleSliderCommit}
                          setIsFollowingLive={setIsFollowingLive}
                        />
                      </div>

                      <div className="lg:w-1/3 flex flex-col min-h-0">
                        <h3 className="text-lg font-semibold text-gray-700 mb-4">
                          Chronologie
                        </h3>
                        {isDeliveriesDataReady ? (
                          <FleetTimeline
                            orders={selectedTour.orders}
                            partOrdersEntities={partOrdersEntities}
                            currentStopForSelectedTour={
                              currentStopForSelectedTour
                            }
                            geofenceInfoMap={geofenceInfoMap}
                          />
                        ) : (
                          <div className="flex-1 flex items-center justify-center">
                            <p className="text-gray-500">
                              Chargement des données de livraison...
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-6 bg-white border rounded-lg shadow-sm flex items-center justify-center h-full text-gray-500">
                  {!isLoadingTours && filteredTours.length > 0
                    ? 'Sélectionnez une tournée dans la liste pour voir les détails.'
                    : !isLoadingTours && filteredTours.length === 0
                    ? 'Aucune tournée ne correspond à vos critères de recherche.'
                    : 'Chargement...'}
                </div>
              )}
            </div>
          </div>
        </div>
      </SiteWrapper>
    </>
  );
}
